/**
 * Centralized capability registry — single source of truth for all tester pool dimensions.
 *
 * Every consumer (validation, Slack display, UI, MCP, CLI) imports from here
 * instead of maintaining its own label maps. To add a new capability dimension:
 * 1. Define it here
 * 2. Add TS fields to TesterPoolRequirements + TesterProfile + JobCreationParams
 * 3. Add a DB migration
 * 4. TypeScript compiler guides the rest
 */

import { TESTER_DEVICES, TESTER_LANGUAGES, type TesterDevice, type TesterLanguage } from './tester.types.js';

// ============================================================================
// Dimension Type Definitions
// ============================================================================

/**
 * Match mode for multi-select capabilities:
 * - 'any': tester needs at least ONE of the required values (OR logic, e.g., devices)
 * - 'all': tester needs ALL of the required values (AND logic, e.g., languages)
 */
export type MatchMode = 'any' | 'all';

export interface MultiSelectCapability<V extends string = string> {
  readonly type: 'multi-select';
  readonly matchMode: MatchMode;
  readonly values: readonly V[];
  /** Short labels for UI checkboxes and validation messages (e.g., "iOS", "English") */
  readonly labels: Readonly<Record<V, string>>;
  /** Contextual labels for Slack job postings (e.g., "iOS", "English speaker") */
  readonly slackLabels: Readonly<Record<V, string>>;
  /** Category name used in missing-requirement messages (e.g., "device" → "Missing device(s): ...") */
  readonly categoryName: string;
  /** Section heading in the UI (e.g., "Device", "Language") */
  readonly sectionLabel: string;
  /** Tooltip text explaining the matching behavior to users */
  readonly tooltip: string;
}

export interface BooleanCapability {
  readonly type: 'boolean';
  /** Label shown in Slack job postings (e.g., "Social video creator") */
  readonly slackLabel: string;
  /** Message shown when tester doesn't meet this requirement */
  readonly missingMessage: string;
  /** Checkbox label in UI (e.g., "Require social video creation capability") */
  readonly uiLabel: string;
}

// ============================================================================
// Multi-Select Dimensions
// ============================================================================

export const DEVICE_CAPABILITY: MultiSelectCapability<TesterDevice> = {
  type: 'multi-select',
  matchMode: 'any',
  values: TESTER_DEVICES,
  labels: { ios: 'iOS', android: 'Android', pc: 'PC', mac: 'Mac' },
  slackLabels: { ios: 'iOS', android: 'Android', pc: 'PC', mac: 'Mac' },
  categoryName: 'device',
  sectionLabel: 'Device',
  tooltip: 'Tester must have at least one of the selected devices',
};

export const LANGUAGE_CAPABILITY: MultiSelectCapability<TesterLanguage> = {
  type: 'multi-select',
  matchMode: 'all',
  values: TESTER_LANGUAGES,
  labels: { english: 'English', spanish: 'Spanish' },
  slackLabels: { english: 'English speaker', spanish: 'Spanish speaker' },
  categoryName: 'language',
  sectionLabel: 'Language',
  tooltip: 'Tester must speak all selected languages',
};

// ============================================================================
// Boolean Dimensions
// ============================================================================

export const SOCIAL_VIDEOS_CAPABILITY: BooleanCapability = {
  type: 'boolean',
  slackLabel: 'Social video creator',
  missingMessage: 'Social video creation required',
  uiLabel: 'Require social video creation capability',
};

/**
 * Sideload tester pool. Testers in this pool can install mobile app builds
 * directly on their device — Android APKs (developer mode + unknown sources)
 * and/or iOS IPAs (TestFlight or signed enterprise builds).
 *
 * Jobs that require this capability MUST also declare exactly one of `'ios'`
 * or `'android'` in `requiredDevices` so the build is targeted at a platform
 * the matched tester actually has. The Slack post and other surfaces compose
 * the platform suffix at render time (e.g. "Sideload (iOS)") rather than
 * baking it into `slackLabel` here.
 */
export const SIDELOAD_INSTALL_CAPABILITY: BooleanCapability = {
  type: 'boolean',
  slackLabel: 'Sideload installer',
  missingMessage: 'Sideload capability required (Android APK or iOS IPA)',
  uiLabel: 'Require sideload capability (Android APK or iOS IPA)',
};

/**
 * Slack instruction appended only for Runhuman dual-device self-tests
 * (jobs with `requiresRunhumanApkInstall: true`). Generic APK-install jobs
 * do NOT get this line — their flow is single-device.
 */
export const RUNHUMAN_SELF_TEST_SLACK_INSTRUCTION =
  ':iphone: *Dual-device test:* Claim on your computer, test on your Android device';
